import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  KeyResultI,
  KeyResultsResponse,
  KeyResultUpdateInfo,
} from '~src/store/okrs/keyResultStream/interfaces';
import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import UpdateKeyResult from '~src/modules/okrs/objectivesStream/updateKeyResult/UpdateKeyResult';
import Dialog from '~src/shared/dialogs/dialog/Dialog';
import logger from '~src/services/logger/LlsLogger';
import { KeyResultUpdate, updateKeyResultAction } from '~src/store/okrs/keyResults/actions';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { showApiError } from '~src/shared/api/notification';
import { ErrRespItem } from '~src/shared/errors/interfaces';

interface DispatchProps {
  updateKeyResult(keyResultId: string, updateObj: KeyResultUpdate): ApiReduxResp<KeyResultsResponse>;
}

interface OwnProps {
  keyResult: KeyResultI;
  onUpdateKeyResult?: () => void;
}

type Props = OwnProps & DispatchProps;

const KeyResultUpdateButton: FC<Props> = ({ keyResult, ...props }) => {
  const [showModal, setShowModal] = useState(false);

  const update = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowModal(true);
  };

  const updateKeyResult = ({ id, progressValue, comment }: KeyResultUpdateInfo) => {
    setSubmitLoading(true);
    if (typeof progressValue !== 'number' || Number.isNaN(progressValue)) {
      return Promise.reject('progress parameter is invalid (probably null / undefined / NaN)');
    }
    const trimmedComment = comment ? comment.trim() : comment;

    return props
      .updateKeyResult(id, { comment: trimmedComment, progress: progressValue })
      .then(resp => {
        if (resp.error) {
          resp.error.response.data.forEach((item: ErrRespItem) => {
            showApiError(item);
          });
          return resp;
        }

        if (props.onUpdateKeyResult) {
          props.onUpdateKeyResult();
        }
        setShowModal(false);
      })
      .catch((error: any) => {
        logger.info('Error updating key result', {
          error,
          info: {
            componentStack: 'KeyResultUpdateButton.tsx',
          },
        });
        throw new Error('Error updating key result');
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const [submitLoading, setSubmitLoading] = useState(false);

  const dontTriggerUpdate = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const [t] = useTranslation();
  return (
    <>
      <div onClick={dontTriggerUpdate}>
        <Dialog width={750} show={showModal} onHide={closeModal}>
          <UpdateKeyResult
            inModal
            id={keyResult.id}
            objectiveId={keyResult.objective_id}
            name={keyResult.name}
            progressValue={keyResult.progress}
            targetValue={keyResult.max}
            startValue={keyResult.min}
            onSubmitUpdateKeyResult={updateKeyResult}
            submitLoading={submitLoading}
          />
        </Dialog>
      </div>
      <PrimaryButton data-translation="x.okrs.key-result.item.button.update" onClick={update}>
        {t('x.okrs.key-result.item.button.update')}
      </PrimaryButton>
    </>
  );
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  updateKeyResult: (keyResultId, updateObj) => dispatch(updateKeyResultAction(keyResultId, updateObj)),
});

const KeyResultUpdateButtonConnected = connect(null, mapDispatchToProps)(KeyResultUpdateButton);
export default KeyResultUpdateButtonConnected;
